import { sendNotificationEmail } from './notificationService';

/**
 * Trigger notification for new lead
 * @param {Object} leadData - The lead data
 */
export const triggerNewLeadNotification = async (leadData) => {
  try {
    const notificationData = {
      leadName: `${leadData.contact?.firstName || ''} ${leadData.contact?.lastName || ''}`.trim(),
      company: leadData.company,
      email: leadData.contact?.email,
      phone: leadData.contact?.phone,
      leadSource: leadData.leadSource,
      estimatedRevenue: leadData.estimatedRevenue,
      stage: leadData.stage,
      accountId: leadData.id
    };

    await sendNotificationEmail('newLead', notificationData);
    console.log('✅ New Lead notification sent successfully');
  } catch (error) {
    console.error('❌ Error sending new lead notification:', error);
  }
};

/**
 * Trigger notification for new client
 * @param {Object} clientData - The client data
 */
export const triggerNewClientNotification = async (clientData) => {
  try {
    const notificationData = {
      clientName: `${clientData.contact?.firstName || ''} ${clientData.contact?.lastName || ''}`.trim(),
      company: clientData.company,
      email: clientData.contact?.email,
      phone: clientData.contact?.phone,
      stage: clientData.stage,
      contractStart: clientData.contractStart,
      contractEnd: clientData.contractEnd,
      accountId: clientData.id
    };

    await sendNotificationEmail('newClient', notificationData);
    console.log('✅ New Client notification sent successfully');
  } catch (error) {
    console.error('❌ Error sending new client notification:', error);
  }
};

/**
 * Trigger notification for new message
 * @param {Object} messageData - The message data
 */
export const triggerNewMessageNotification = async (messageData) => {
  try {
    const notificationData = {
      sender: messageData.sender,
      senderId: messageData.senderId, // Pass senderId so notification service can filter it out
      subject: messageData.subject,
      message: messageData.content,
      conversationId: messageData.conversationId,
      participants: messageData.participants,
      timestamp: messageData.timestamp
    };

    await sendNotificationEmail('newMessage', notificationData);
    console.log('✅ New Message notification sent successfully');
  } catch (error) {
    console.error('❌ Error sending new message notification:', error);
  }
};

/**
 * Trigger notification for new question
 * @param {Object} questionData - The question data
 */
export const triggerNewQuestionNotification = async (questionData) => {
  try {
    const notificationData = {
      questioner: questionData.questioner,
      question: questionData.question,
      category: questionData.category,
      priority: questionData.priority,
      assigneeId: questionData.assigneeId,
      assigneeName: questionData.assigneeName,
      accountId: questionData.accountId,
      accountName: questionData.accountName,
      timestamp: questionData.timestamp
    };

    await sendNotificationEmail('newQuestion', notificationData);
    console.log('✅ New Question notification sent successfully');
  } catch (error) {
    console.error('❌ Error sending new question notification:', error);
  }
};


/**
 * Trigger notification for new task
 * @param {Object} taskData - The task data
 */
export const triggerNewTaskNotification = async (taskData) => {
  try {
    const assignees = taskData.assignees || (taskData.assignee ? [taskData.assignee] : []);
    const createdBy = taskData.createdBy;

    // Skip notification if creator is the only assignee
    if (assignees.length === 1 && assignees[0] === createdBy) {
      console.log('⏭️ Skipping task notification: creator is the only assignee');
      return {
        success: true,
        message: 'Notification skipped: creator is the only assignee',
        sentCount: 0
      };
    }

    const notificationData = {
      taskTitle: taskData.taskTitle || taskData.title,
      description: taskData.description,
      assignees: assignees,
      dueDate: taskData.dueDate,
      priority: taskData.priority,
      createdBy: createdBy,
      accountId: taskData.accountId,
      timestamp: taskData.timestamp
    };

    await sendNotificationEmail('newTask', notificationData);
    console.log('✅ New Task notification sent successfully');
  } catch (error) {
    console.error('❌ Error sending new task notification:', error);
  }
};

/**
 * Trigger notification for fulfillment updates
 * @param {string} fulfillmentType - The type of fulfillment (technology, hr, marketing, general)
 * @param {Object} fulfillmentData - The fulfillment data
 */
export const triggerFulfillmentNotification = async (fulfillmentType, fulfillmentData) => {
  try {
    // Map fulfillment types to notification types
    const notificationTypeMap = {
      'technology': 'technologyFulfillment',
      'hr': 'hrFulfillment', 
      'marketing': 'marketingFulfillment',
      'general': 'generalFulfillment'
    };
    
    const notificationType = notificationTypeMap[fulfillmentType] || fulfillmentType;
    
    const notificationData = {
      type: fulfillmentData.type,
      name: fulfillmentData.name,
      description: fulfillmentData.description,
      requestedBy: fulfillmentData.requestedBy,
      company: fulfillmentData.company,
      primaryWeek: fulfillmentData.primaryWeek,
      alternativeWeek: fulfillmentData.alternativeWeek,
      category: fulfillmentData.category,
      softwares: fulfillmentData.softwares,
      pluginId: fulfillmentData.pluginId,
      update: fulfillmentData.update,
      status: fulfillmentData.status,
      assignee: fulfillmentData.assignee,
      dueDate: fulfillmentData.dueDate,
      priority: fulfillmentData.priority,
      timestamp: fulfillmentData.timestamp
    };

    // generalFulfillment email template shows `update`, not `description`
    if (notificationType === 'generalFulfillment' && notificationData.description && !notificationData.update) {
      notificationData.update = notificationData.description;
    }

    await sendNotificationEmail(notificationType, notificationData);
    console.log(`✅ ${notificationType} notification sent successfully`);
  } catch (error) {
    console.error(`❌ Error sending ${fulfillmentType} notification:`, error);
  }
};

/**
 * Trigger notification for community post
 * @param {Object} postData - The post data
 */
export const triggerCommunityPostNotification = async (postData) => {
  try {
    const notificationData = {
      author: postData.author,
      title: postData.title,
      content: postData.content,
      category: postData.category,
      timestamp: postData.timestamp
    };

    await sendNotificationEmail('communityPost', notificationData);
    console.log('✅ Community Post notification sent successfully');
  } catch (error) {
    console.error('❌ Error sending community post notification:', error);
  }
};

/**
 * Trigger notification for NDA signed
 * @param {Object} ndaData - The NDA data
 */
export const triggerNDASignedNotification = async (ndaData) => {
  try {
    const notificationData = {
      clientName: ndaData.clientName,
      company: ndaData.company,
      signedDate: ndaData.signedDate,
      documentId: ndaData.documentId,
      status: ndaData.status,
      documentType: ndaData.documentType || 'NDA'
    };

    await sendNotificationEmail('ndaSigned', notificationData);
    console.log('✅ NDA Signed notification sent successfully');
  } catch (error) {
    console.error('❌ Error sending NDA signed notification:', error);
  }
};

/**
 * Trigger notification for contract signed
 * @param {Object} contractData - The contract data
 */
export const triggerContractSignedNotification = async (contractData) => {
  try {
    const notificationData = {
      clientName: contractData.clientName,
      company: contractData.company,
      contractType: contractData.contractType,
      signedDate: contractData.signedDate,
      documentId: contractData.documentId,
      status: contractData.status
    };

    await sendNotificationEmail('contractSigned', notificationData);
    console.log('✅ Contract Signed notification sent successfully');
  } catch (error) {
    console.error('❌ Error sending contract signed notification:', error);
  }
};

/**
 * Trigger notification for portal signup
 * @param {Object} signupData - The signup data
 */
export const triggerPortalSignupNotification = async (signupData) => {
  try {
    // Support both 'name' and 'userName' fields for backward compatibility
    const userName = signupData.name || signupData.userName || '';
    
    const notificationData = {
      name: userName,
      email: signupData.email,
      company: signupData.company,
      signupDate: signupData.signupDate,
      status: signupData.status,
      leadId: signupData.leadId,
      timestamp: signupData.timestamp
    };

    await sendNotificationEmail('portalSignup', notificationData);
    console.log('✅ Portal Signup notification sent successfully');
  } catch (error) {
    console.error('❌ Error sending portal signup notification:', error);
  }
};

/**
 * Trigger notification for summit registration
 * @param {Object} registrationData - The registration data
 */
export const triggerSummitRegistrationNotification = async (registrationData) => {
  try {
    const notificationData = {
      registrantName: registrationData.registrantName,
      email: registrationData.email,
      company: registrationData.company,
      summitName: registrationData.summitName,
      registrationDate: registrationData.registrationDate,
      ticketType: registrationData.ticketType
    };

    await sendNotificationEmail('summitRegistration', notificationData);
    console.log('✅ Summit Registration notification sent successfully');
  } catch (error) {
    console.error('❌ Error sending summit registration notification:', error);
  }
};

/**
 * Trigger notification for flagged lead
 * @param {Object} leadData - The lead data
 * @param {Object} flagInfo - Information about why the lead was flagged
 */
export const triggerFlaggedLeadNotification = async (leadData, flagInfo) => {
  try {
    const notificationData = {
      leadName: `${leadData.contact?.firstName || ''} ${leadData.contact?.lastName || ''}`.trim() || 'Unknown',
      company: leadData.company || 'No Company',
      email: leadData.contact?.email || leadData.email,
      phone: leadData.contact?.phone || leadData.phone,
      stage: leadData.stage || 'Lead Received',
      leadId: leadData.id,
      daysSinceContact: flagInfo?.daysSinceContact || flagInfo?.daysSinceCreation || 0,
      daysUntilFlagged: flagInfo?.daysUntilFlagged || 0,
      reason: flagInfo?.reason || 'Lead has exceeded contact threshold'
    };

    await sendNotificationEmail('flaggedLead', notificationData);
    console.log('✅ Flagged Lead notification sent successfully');
  } catch (error) {
    console.error('❌ Error sending flagged lead notification:', error);
  }
};

/**
 * Trigger notification for assessment complete
 * @param {Object} assessmentData - The assessment completion data
 */
export const triggerAssessmentCompleteNotification = async (assessmentData) => {
  try {
    const notificationData = {
      leadName: `${assessmentData.contact?.firstName || ''} ${assessmentData.contact?.lastName || ''}`.trim() || assessmentData.leadName || 'Unknown',
      company: assessmentData.company || 'No Company',
      email: assessmentData.contact?.email || assessmentData.email,
      phone: assessmentData.contact?.phone || assessmentData.phone,
      leadId: assessmentData.leadId || assessmentData.id,
      completedDate: assessmentData.completedDate || new Date().toISOString(),
      substatus: assessmentData.substatus || 'Assessment Done',
      logoUrl: assessmentData.companyLogo || assessmentData.crmCompanyLogo || '',
      timestamp: assessmentData.timestamp || new Date().toISOString()
    };

    await sendNotificationEmail('assessmentComplete', notificationData);
    console.log('✅ Assessment Complete notification sent successfully');
  } catch (error) {
    console.error('❌ Error sending assessment complete notification:', error);
  }
};